import React, { useState, useMemo, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight, FaWallet, FaReceipt, FaExchangeAlt, FaGem } from 'react-icons/fa';
import {
    useAssets, useLiabilities, useEMIs, useNetWorthHistory, useFixedExpenses,
    useOperationalExpenses, useSpendingTargets, useGamification, useIncomeTargets,
    useTransactions, useCashInHand, useWishlistItems, useHolidays,
} from '../hooks/useFirestore';
import { useFinance } from '../context/FinanceContext';
import IncomeTab from '../components/wealth/IncomeTab';
import ExpensesTab from '../components/wealth/ExpensesTab';
import CashFlowTab from '../components/wealth/CashFlowTab';
import VisionTab from '../components/wealth/VisionTab';

// ── Light Theme Constants ────────────────────────────────────────

const L = {
    page: 'bg-gradient-to-br from-slate-50 to-white min-h-[calc(100vh-3.5rem)] -m-6 p-6',
    card: 'bg-white rounded-2xl shadow-sm border border-gray-100 p-6',
    cardHover: 'hover:shadow-md hover:border-gray-200 transition-all',
    text: 'text-gray-900',
    textMuted: 'text-gray-500',
    textLabel: 'text-[10px] text-gray-400 uppercase tracking-widest',
    input: 'w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-300',
    btnPrimary: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white font-bold rounded-xl shadow-sm text-sm px-6 py-2.5',
    btnSecondary: 'bg-gray-100 text-gray-700 font-semibold rounded-xl text-sm px-4 py-2 hover:bg-gray-200',
    tabActive: 'bg-blue-500 text-white shadow-sm',
    tabInactive: 'text-gray-500 hover:text-gray-700 hover:bg-gray-100',
    positive: 'text-emerald-600',
    negative: 'text-rose-500',
    accent: 'text-blue-600',
    modal: 'fixed inset-0 z-[200] bg-black/30 backdrop-blur-sm flex items-center justify-center p-6',
    modalCard: 'bg-white w-full max-w-lg rounded-2xl p-8 shadow-xl border border-gray-100 space-y-6',
};

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#F97316'];

const LIGHT_TOOLTIP = {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    color: '#111827',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
};

// ── Date Helpers ─────────────────────────────────────────────────

function startOfDay(d) { const r = new Date(d); r.setHours(0, 0, 0, 0); return r; }
function endOfDay(d) { const r = new Date(d); r.setHours(23, 59, 59, 999); return r; }
function startOfWeek(d) {
    const r = new Date(d);
    const day = r.getDay();
    const diff = day === 0 ? 6 : day - 1; // Monday start
    r.setDate(r.getDate() - diff);
    r.setHours(0, 0, 0, 0);
    return r;
}
function endOfWeek(d) {
    const r = startOfWeek(d);
    r.setDate(r.getDate() + 6);
    r.setHours(23, 59, 59, 999);
    return r;
}
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function endOfMonth(d) { return new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999); }

function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }
function addWeeks(d, n) { return addDays(d, n * 7); }
function addMonths(d, n) { const r = new Date(d); r.setMonth(r.getMonth() + n); return r; }

function formatDate(d, mode) {
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const fullMonths = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

    if (mode === 'day') {
        return `${days[d.getDay()]}, ${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
    }
    if (mode === 'week') {
        const ws = startOfWeek(d);
        const we = endOfWeek(d);
        return `${months[ws.getMonth()]} ${ws.getDate()} - ${months[we.getMonth()]} ${we.getDate()}, ${we.getFullYear()}`;
    }
    return `${fullMonths[d.getMonth()]} ${d.getFullYear()}`;
}

function getDateRange(mode, selectedDate) {
    const d = new Date(selectedDate);
    if (mode === 'day') return { start: startOfDay(d), end: endOfDay(d) };
    if (mode === 'week') return { start: startOfWeek(d), end: endOfWeek(d) };
    return { start: startOfMonth(d), end: endOfMonth(d) };
}

// ── Calendar Navigator ───────────────────────────────────────────

const CalendarNavigator = ({ mode, setMode, selectedDate, onDateChange }) => {
    const handlePrev = () => {
        if (mode === 'day') onDateChange(addDays(selectedDate, -1));
        else if (mode === 'week') onDateChange(addWeeks(selectedDate, -1));
        else onDateChange(addMonths(selectedDate, -1));
    };
    const handleNext = () => {
        if (mode === 'day') onDateChange(addDays(selectedDate, 1));
        else if (mode === 'week') onDateChange(addWeeks(selectedDate, 1));
        else onDateChange(addMonths(selectedDate, 1));
    };

    return (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-3 flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Mode Toggle */}
            <div className="flex bg-gray-100 rounded-xl p-1">
                {['day', 'week', 'month'].map(m => (
                    <button key={m} onClick={() => setMode(m)}
                        className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-all capitalize ${
                            mode === m ? 'bg-blue-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
                        }`}>{m}</button>
                ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-3">
                <button onClick={handlePrev} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <FaChevronLeft className="text-gray-400 text-xs" />
                </button>
                <div className="min-w-[220px] text-center">
                    <span className="text-sm font-semibold text-gray-900">{formatDate(selectedDate, mode)}</span>
                </div>
                <button onClick={handleNext} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                    <FaChevronRight className="text-gray-400 text-xs" />
                </button>
            </div>

            {/* Today button */}
            <button onClick={() => { onDateChange(new Date()); }} className="text-xs font-semibold text-blue-600 hover:text-blue-700 px-3 py-1.5 hover:bg-blue-50 rounded-lg transition-colors">
                Today
            </button>
        </div>
    );
};

// ── Main Component ───────────────────────────────────────────────

const WealthEngine = () => {
    // Tab & calendar state
    const [activeTab, setActiveTab] = useState('income');
    const [calendarMode, setCalendarMode] = useState('month');
    const [selectedDate, setSelectedDate] = useState(new Date());

    // Paint the parent <main> white while this page is mounted
    useEffect(() => {
        const main = document.querySelector('main');
        if (main) {
            main.style.background = 'linear-gradient(135deg, #f8fafc, #ffffff)';
        }
        return () => { if (main) main.style.background = ''; };
    }, []);

    // All hooks
    const { data: assets, loading: assetsLoading } = useAssets();
    const { data: liabilities, loading: liabLoading } = useLiabilities();
    const { data: emis } = useEMIs();
    const { data: netWorthHistory } = useNetWorthHistory();
    const { data: fixedExpenses } = useFixedExpenses();
    const currentMonth = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    const { data: operationalExpenses } = useOperationalExpenses(currentMonth);
    const { targets: spendingTargets, updateTargets: updateSpendingTargets } = useSpendingTargets();
    const { gamification, updateGamification } = useGamification();
    const { targets: incomeTargets, updateTargets: updateIncomeTargets } = useIncomeTargets();
    const { stats, targets: finTargets, settings } = useFinance();

    const { cashInHand, updateCashInHand } = useCashInHand();
    const { data: wishlistItems } = useWishlistItems();
    const { data: holidays } = useHolidays();

    // Own transactions hook synced to WealthEngine's calendar (not FinanceContext's selectedMonth)
    const selectedMonthStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, '0')}`;
    const { data: transactions } = useTransactions(selectedMonthStr);

    // Shared computed
    const totalAssets = useMemo(() => (assets || []).reduce((s, a) => s + (Number(a.value) || 0), 0), [assets]);
    const totalLiabilities = useMemo(() => (liabilities || []).reduce((s, l) => s + (Number(l.amount) || 0), 0), [liabilities]);
    const netWorth = totalAssets - totalLiabilities;
    const dateRange = useMemo(() => getDateRange(calendarMode, selectedDate), [calendarMode, selectedDate]);

    // Shared props for tabs
    const sharedProps = {
        L, CHART_COLORS, LIGHT_TOOLTIP,
        assets, liabilities, emis, netWorthHistory,
        fixedExpenses, operationalExpenses,
        stats, transactions, finTargets, settings,
        spendingTargets, updateSpendingTargets,
        incomeTargets, updateIncomeTargets,
        gamification, updateGamification,
        totalAssets, totalLiabilities, netWorth,
        calendarMode, selectedDate, dateRange,
        assetsLoading, liabLoading,
        cashInHand, updateCashInHand,
        wishlistItems, holidays,
    };

    const tabs = [
        { key: 'income', label: 'Income', icon: <FaWallet className="text-xs" /> },
        { key: 'expenses', label: 'Expenses', icon: <FaReceipt className="text-xs" /> },
        { key: 'cashflow', label: 'Cash Flow', icon: <FaExchangeAlt className="text-xs" /> },
        { key: 'vision', label: 'Vision Board', icon: <FaGem className="text-xs" /> },
    ];

    return (
        <div className={L.page}>
            {/* Page Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-6">
                <div>
                    <p className={L.textLabel + ' mb-1'}>Financial Command Center</p>
                    <h1 className="text-2xl font-bold tracking-tight text-gray-900">Wealth Engine</h1>
                    <p className="text-gray-500 mt-0.5 text-sm">Income, expenses, analytics & cash flow</p>
                </div>
                <div className="text-right">
                    <p className={L.textLabel + ' mb-1'}>Net Worth</p>
                    <h2 className="text-3xl font-bold text-blue-600">{'\u20B9'}{netWorth.toLocaleString()}</h2>
                </div>
            </div>

            {/* Tab Bar */}
            <div className="bg-gray-100 rounded-2xl p-1.5 mb-5 flex">
                {tabs.map(tab => (
                    <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                        className={`flex-1 py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                            activeTab === tab.key
                                ? 'bg-white text-gray-900 shadow-sm'
                                : 'text-gray-400 hover:text-gray-600'
                        }`}>
                        {tab.icon} {tab.label}
                    </button>
                ))}
            </div>

            {/* Calendar Navigator */}
            <div className="mb-6">
                <CalendarNavigator
                    mode={calendarMode}
                    setMode={setCalendarMode}
                    selectedDate={selectedDate}
                    onDateChange={setSelectedDate}
                />
            </div>

            {/* Tab Content */}
            {activeTab === 'income' && <IncomeTab {...sharedProps} />}
            {activeTab === 'expenses' && <ExpensesTab {...sharedProps} />}
            {activeTab === 'cashflow' && <CashFlowTab {...sharedProps} />}
            {activeTab === 'vision' && <VisionTab {...sharedProps} />}
        </div>
    );
};

export default WealthEngine;
